import React, { useContext, useEffect, useRef, useState } from 'react'
import { Image, ImageStyle, TouchableOpacity, View } from 'react-native'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { Analytics, Bugsnag, CustomEventTypes } from '@src/config'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Input, Radio, Text } from '@components/base'
import {
  cgmG6Image,
  cgmG7Image,
  cgmLibre14dayImage,
  cgmLibre2Image,
  cgmLibre3Image,
  cgmLingoImage,
  cgmSteloImage,
} from '@src/assets/images'
import { NavigatorContext, openUrl, Storage, useDispatchAsync } from '@src/utils'
import { InlineAlert, LoadingIndicator, ScrollViewWithFade } from '@src/components'
import { UiInlineAlert } from '@src/types'
import type { UiFlags } from '@src/types'
import { CommonAuthorizedStackParamList, RootStackParamList } from '@src/navigation/types'
import { userSelector } from '@src/selectors/app'
import { uiFlagsSelector } from '@src/selectors/uiFlags'
import { NavigationContainer } from '../Common/containers'
import { OwnSensorInfoText, OwnSensorOption, OwnSensorSurveyAnswers } from './constants'
import { useSubmitOwnSensorSurvey } from './hooks'

interface SensorSupportData {
  [key: string]:
    | {
        text?: OwnSensorInfoText
        supported: boolean
      }
    | undefined
}

type SensorImageMap = {
  [K in OwnSensorOption]: any
}

const sensorSupportData: SensorSupportData = {
  [OwnSensorOption.Other]: {
    text: OwnSensorInfoText.OtherSensor,
    supported: true,
  },
  [OwnSensorOption.Lingo]: {
    text: OwnSensorInfoText.Lingo,
    supported: true,
  },
  [OwnSensorOption.FreeStyleLibre14Day]: {
    supported: true,
  },
  [OwnSensorOption.FreeStyleLibre2]: {
    supported: true,
  },
  [OwnSensorOption.FreeStyleLibre3]: {
    supported: true,
  },
  [OwnSensorOption.DexcomG6]: {
    supported: true,
  },
  [OwnSensorOption.DexcomG7]: {
    supported: true,
  },
  [OwnSensorOption.DexcomStelo]: {
    supported: true,
  },
}

const sensorImageMap: SensorImageMap = {
  [OwnSensorOption.FreeStyleLibre14Day]: cgmLibre14dayImage,
  [OwnSensorOption.FreeStyleLibre2]: cgmLibre2Image,
  [OwnSensorOption.FreeStyleLibre3]: cgmLibre3Image,
  [OwnSensorOption.Lingo]: cgmLingoImage,
  [OwnSensorOption.DexcomG6]: cgmG6Image,
  [OwnSensorOption.DexcomG7]: cgmG7Image,
  [OwnSensorOption.DexcomStelo]: cgmSteloImage,
  [OwnSensorOption.Other]: undefined,
}

export const SensorSelectionScreen = () => {
  const styles = useStyleSheet(themedStyle)
  const [selectedSensor, setSelectedSensor] = useState<OwnSensorOption | undefined>()
  const [infoText, setInfoText] = useState<OwnSensorInfoText | undefined>()
  const [selectionSupported, setSelectionSupported] = useState<boolean>(false)
  const user = useSelector(userSelector)
  const uiFlags = useSelector(uiFlagsSelector)
  const showLibreIndirectSyncConfirmation = uiFlags.showLibreIndirectSyncConfirmation || false
  const dispatchAsync = useDispatchAsync()
  const submitOwnSensorSurvey = useSubmitOwnSensorSurvey()
  const submittingRef = useRef(false)
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const insets = useSafeAreaInsets()
  const route = useRoute<RouteProp<CommonAuthorizedStackParamList, 'OwnSensorSelection'>>()
  const { nextScreen, params = {} } = route.params

  useEffect(() => {
    setInfoText(undefined)
    setSelectionSupported(false)

    if (!selectedSensor) {
      return
    }

    const selectionData = sensorSupportData[selectedSensor]

    if (!selectionData) {
      Bugsnag.notify(
        new Error(
          `OwnSensor signup flow - sensor selection step - No data found for sensor: ${selectedSensor}`,
        ),
      )
      return
    }

    if (selectedSensor !== OwnSensorOption.Other) {
      setInfoText(selectionData.text)
    }
    setSelectionSupported(selectionData.supported)
  }, [selectedSensor])

  const onNextPress = async () => {
    let shouldShowLibreIndirectSyncConfirmation = showLibreIndirectSyncConfirmation

    if (submittingRef.current) {
      return
    }

    if (selectedSensor === OwnSensorOption.Other) {
      setInfoText(OwnSensorInfoText.OtherSensor)
      return
    }

    if (!selectedSensor) {
      Bugsnag.notify(
        new Error(
          `OwnSensor signup flow sensor selection - submission attempted without sensor selected`,
        ),
      )
      return
    }

    Analytics.track(CustomEventTypes.OwnSensorSelected, { sensorType: selectedSensor })

    Storage.set(Storage.ONBOARDING_SENSOR_TYPE_SELECTED_KEY, OwnSensorSurveyAnswers[selectedSensor])
    if (user) {
      enableLoader()
      submittingRef.current = true
      try {
        await submitOwnSensorSurvey()
        // Needed to get the most recent sensor mode kind
        await dispatchAsync({
          type: 'users/fetch',
        })
        const fetchedUiFlags = await dispatchAsync<Partial<UiFlags>>({
          type: 'uiFlags/fetch',
        })
        shouldShowLibreIndirectSyncConfirmation =
          fetchedUiFlags.showLibreIndirectSyncConfirmation || false
      } catch {
        return // don't navigate to the nextScreen if survey submission fails
      } finally {
        disableLoader()
        submittingRef.current = false
      }
    }

    if (
      shouldShowLibreIndirectSyncConfirmation &&
      nextScreen === 'OwnSensorFreeTrialSubscriptionOffer'
    ) {
      navigation.navigate('LibreIndirectSyncConfirmation')
      return
    }

    navigation.navigate(nextScreen, params, { pop: true })
  }

  const sensorKindClicked = (sensor: OwnSensorOption) => {
    if (sensor === selectedSensor) {
      return
    }

    setSelectedSensor(sensor)
  }

  const onMoreInfoPress = () => {
    if (!infoText || !selectedSensor) {
      return
    }

    openUrl(
      'https://support.nutrisense.io/en_us/bring-your-own-sensor-program:-sensor-limitations-H1wDkqRkgl',
    )
  }

  return (
    <NavigationContainer title="Bringing My Own Sensor">
      <View style={[styles.container, { paddingBottom: insets.bottom + 16 }]}>
        <Text type="large" bold style={styles.titleText}>
          Choose your sensor model from the list below.
        </Text>
        <View style={styles.content}>
          <ScrollViewWithFade style={styles.scrollView}>
            {Object.values(OwnSensorOption).map((sensor) => (
              <TouchableOpacity
                key={sensor}
                accessibilityLabel={sensor}
                onPress={() => sensorKindClicked(sensor)}
                style={styles.selectionOption}
              >
                <View style={styles.labelWithImage}>
                  <Image
                    source={sensorImageMap[sensor]?.imageSource}
                    style={styles.sensorImage as ImageStyle}
                  />
                  <Text
                    type="regular"
                    numberOfLines={1}
                    accessibilityLabel={sensor}
                    adjustsFontSizeToFit
                    minimumFontScale={0.8}
                    style={styles.sensorText}
                  >
                    {sensor}
                  </Text>
                </View>
                <Radio
                  style={styles.radio}
                  checked={selectedSensor === sensor}
                  onPress={() => sensorKindClicked(sensor)}
                />
              </TouchableOpacity>
            ))}
          </ScrollViewWithFade>
          {selectedSensor === OwnSensorOption.Other && (
            <Input style={styles.otherSensorInput} placeholder="Type your answer" />
          )}
          <View style={styles.actionAndInfoContainer}>
            {!!infoText && (
              <TouchableOpacity
                accessibilityLabel="Own Sensor learn more"
                activeOpacity={0.7}
                onPress={onMoreInfoPress}
              >
                <InlineAlert category={UiInlineAlert.Warning} message={infoText} />
              </TouchableOpacity>
            )}
            {!!selectedSensor && selectionSupported && (
              <Button
                type="primary"
                accessibilityLabel="Next"
                size="block"
                onPress={onNextPress}
                accessoryRight={submittingRef.current ? <LoadingIndicator size="small" /> : null}
                disabled={submittingRef.current}
              >
                Next
              </Button>
            )}
          </View>
        </View>
      </View>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    backgroundColor: 'theme.background',
  },
  content: {
    flex: 1,
    justifyContent: 'space-between',
  },
  scrollView: {
    flex: 1,
  },
  titleText: {
    marginBottom: 16,
  },
  radio: {
    marginLeft: 16,
  },
  sensorImage: {
    maxWidth: 40,
    maxHeight: 40,
    resizeMode: 'contain',
  },
  selectionOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'theme.surface.base1',
    padding: 16,
    marginBottom: 8,
  },
  labelWithImage: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    gap: 8,
  },
  sensorText: {
    flexShrink: 1,
    maxWidth: '100%',
  },
  actionAndInfoContainer: {
    gap: 8,
  },
  otherSensorInput: {
    marginBottom: 10,
  },
})
